import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface KBListProps {
  token: string;
  onNavigateToPlayground?: (kbId: number) => void;
}

type SourceStatus = 'pending' | 'ok' | 'empty' | 'error';
type SourceType = 'file' | 'url';

interface KnowledgeBaseSource {
  id: number;
  sourceType: SourceType;           // 'file' | 'url'
  originalFilename?: string | null;
  storedPath?: string | null;       // e.g., 'uploads/xyz.pdf'
  size?: number | null;
  mimeType?: string | null;
  fileExt?: string | null;
  url?: string | null;
  loader?: string | null;           // 'langchain' | 'langchain_url' | 'fallback'
  status: SourceStatus;             // 'pending' | 'ok' | 'empty' | 'error'
  errorMessage?: string | null;
  checksum?: string | null;
  language?: string | null;
  version?: number | null;
}

interface KnowledgeBase {
  id: number;
  name: string;
  description?: string;
  client?: string | null;
  ipAddress?: string | null;
  browser?: string | null;
  userAgent?: string | null;
  kbType?: string;                  // 'mixed' | 'website' | 'pdf' | 'text' ...
  embedProvider?: string | null;    // 'hf' | 'openai'
  embedModel?: string | null;       // 'all-MiniLM-L6-v2' ...
  indexed: boolean;
  processingStatus?: string;        // 'pending' | 'processing' | 'completed' | 'failed'
  chunkCount?: number | null;
  embeddingDim?: number | null;
  pipelineVersion?: number | null;
  metadata?: any;
  createdAt?: string;
  updatedAt?: string;
  sources?: KnowledgeBaseSource[];
}


const KBList: React.FC<KBListProps> = ({ token, onNavigateToPlayground }) => {
  const navigate = useNavigate();
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [deleteSourceLoading, setDeleteSourceLoading] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchKnowledgeBases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    setError(null);
    try {
  const res = await api.get(`/api/kb/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKnowledgeBases(res.data.knowledgeBases || []);
    } catch (err: any) {
      console.error('Error fetching knowledge bases:', err);
      setError(err.response?.data?.message || 'Failed to fetch knowledge bases');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (kbId: number, kbName: string) => {
    if (!window.confirm(`Delete "${kbName}"? This action cannot be undone.`)) return;

    setDeleteLoading(kbId);
    try {
      await api.delete(`/api/kb/delete/${kbId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKnowledgeBases((prev) => prev.filter((kb) => kb.id !== kbId));
      alert('Knowledge base deleted successfully');
    } catch (err: any) {
      console.error('Error deleting knowledge base:', err);
      alert(err.response?.data?.message || 'Failed to delete knowledge base');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteSource = async (kbId: number, source: KnowledgeBaseSource) => {
    const label =
      source.sourceType === 'file'
        ? (source.originalFilename || `file #${source.id}`)
        : (source.url || `url #${source.id}`);
    if (!window.confirm(`Delete source "${label}" from this knowledge base?`)) return;

    setDeleteSourceLoading(source.id);
    try {
      const res = await api.delete(`/api/kb/source/${source.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedKb = res.data?.kb as KnowledgeBase | null | undefined;
      if (updatedKb) {
        setKnowledgeBases((prev) => prev.map((kb) => (kb.id === kbId ? updatedKb : kb)));
      } else {
        await fetchKnowledgeBases();
      }
    } catch (err: any) {
      console.error('Error deleting knowledge base source:', err);
      alert(err.response?.data?.message || 'Failed to delete knowledge base source');
    } finally {
      setDeleteSourceLoading(null);
    }
  };

  // ---------- helpers ----------
  const formatFileSize = (value?: number | string | null) => {
    const bytes = Number(value);
    if (!bytes || Number.isNaN(bytes) || bytes < 0) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString();
  };

  const getMetadata = (kb: KnowledgeBase) =>
    kb.metadata && typeof kb.metadata === 'object' && !Array.isArray(kb.metadata)
      ? kb.metadata
      : {};

  const getMetadataString = (metadata: Record<string, unknown>, keys: string[]) => {
    for (const key of keys) {
      const value = metadata[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return undefined;
  };

  const getClientValue = (kb: KnowledgeBase) => {
    const metadata = getMetadata(kb);
    const metadataClientObj =
      metadata.client && typeof metadata.client === 'object' && !Array.isArray(metadata.client)
        ? (metadata.client as Record<string, unknown>)
        : null;
    return (
      kb.client ||
      getMetadataString(metadata, ['client', 'clientName']) ||
      getMetadataString(metadataClientObj || {}, ['client', 'name']) ||
      'vsknowledgebase'
    );
  };

  const getIpValue = (kb: KnowledgeBase) => {
    const metadata = getMetadata(kb);
    const metadataClientObj =
      metadata.client && typeof metadata.client === 'object' && !Array.isArray(metadata.client)
        ? (metadata.client as Record<string, unknown>)
        : null;
    return (
      kb.ipAddress ||
      getMetadataString(metadata, ['ipAddress', 'ip', 'ip_address']) ||
      getMetadataString(metadataClientObj || {}, ['ipAddress', 'ip', 'ip_address']) ||
      'N/A'
    );
  };

  const getBrowserValue = (kb: KnowledgeBase) => {
    const metadata = getMetadata(kb);
    const metadataClientObj =
      metadata.client && typeof metadata.client === 'object' && !Array.isArray(metadata.client)
        ? (metadata.client as Record<string, unknown>)
        : null;
    return (
      kb.browser ||
      getMetadataString(metadata, ['browser']) ||
      getMetadataString(metadataClientObj || {}, ['browser']) ||
      'N/A'
    );
  };

  const getUserAgentValue = (kb: KnowledgeBase) => {
    const metadata = getMetadata(kb);
    const metadataClientObj =
      metadata.client && typeof metadata.client === 'object' && !Array.isArray(metadata.client)
        ? (metadata.client as Record<string, unknown>)
        : null;
    return (
      kb.userAgent ||
      getMetadataString(metadata, ['userAgent', 'user_agent']) ||
      getMetadataString(metadataClientObj || {}, ['userAgent', 'user_agent']) ||
      'N/A'
    );
  };

  const handleDownloadSource = async (source: KnowledgeBaseSource) => {
    if (source.sourceType !== 'file' || !source.storedPath) return;
    try {
      const res = await api.get(`/api/kb/download/${source.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const filename =
        source.originalFilename ||
        source.storedPath.split('/').pop() ||
        `source_${source.id}`;
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      console.error('Download failed:', err);
      alert(err?.response?.data?.message || 'Failed to download file');
    }
  };

  const getKbIcon = (kbType?: string) => {
    switch ((kbType || '').toLowerCase()) {
      case 'pdf':
        return <span title="PDF">📄</span>;
      case 'word':
        return <span title="Word">📝</span>;
      case 'image':
        return <span title="Image">🖼️</span>;
      case 'video':
        return <span title="Video">🎬</span>;
      case 'markdown':
        return <span title="Markdown">🧾</span>;
      case 'website':
        return <span title="Website">🌐</span>;
      case 'mixed':
        return <span title="Mixed">🧩</span>;
      default:
        return <span title="Text/Other">📚</span>;
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Ready
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <div className="animate-spin w-3 h-3 mr-1 border border-yellow-800 rounded-full border-t-transparent"></div>
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L10 10.586l1.293-1.293a1 1 0 011.414 1.414L10 13.414l-1.293-1.293a1 1 0 00-1.414-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Pending
          </span>
        );
    }
  };

  // Summaries computed from sources
  const kbSummaries = useMemo(() => {
    return knowledgeBases.map((kb) => {
      const srcs = kb.sources || [];
      const files = srcs.filter((s) => s.sourceType === 'file');
      const urls = srcs.filter((s) => s.sourceType === 'url');
      const totalSize = files.reduce((sum, s) => sum + Number(s.size || 0), 0);
      const firstFileName =
        files[0]?.originalFilename || files[0]?.storedPath?.split('/').pop() || null;
      const firstUrl = urls[0]?.url || null;
      const errors = srcs.filter((s) => s.status === 'error').length;
      const empties = srcs.filter((s) => s.status === 'empty').length;
      return {
        id: kb.id,
        filesCount: files.length,
        urlsCount: urls.length,
        totalSize,
        firstFileName,
        firstUrl,
        errors,
        empties,
      };
    });
  }, [knowledgeBases]);

  const totalBytesAllKBs = useMemo(
    () =>
      knowledgeBases.reduce((grand, kb) => {
        const srcs = kb.sources || [];
        return grand + srcs.filter(s => s.sourceType === 'file').reduce((sum, s) => sum + Number(s.size || 0), 0);
      }, 0),
    [knowledgeBases]
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-lg text-gray-600">Loading knowledge bases...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Knowledge Bases</h3>
              <p className="text-red-600 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchKnowledgeBases}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Knowledge Base Library</h2>
              <p className="text-gray-600 mt-1">Manage your uploaded knowledge bases</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchKnowledgeBases}
                className="bg-gray-100 text-gray-600 px-3 py-2 rounded hover:bg-gray-200 transition"
                title="Refresh"
              >
                ⟳
              </button>
              <span className="text-sm text-gray-500">
                Total Records: {knowledgeBases.length}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        {knowledgeBases.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-4xl mb-3">📂</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Knowledge Bases Found</h3>
            <p className="text-gray-500 mb-4">Upload your first knowledge base to get started.</p>
            <button
              onClick={() => (window.location.hash = 'upload')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              Upload Knowledge Base
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">kb Id</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Knowledge Base</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sources</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Info</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {knowledgeBases.map((kb) => {
                  const sum = kbSummaries.find((s) => s.id === kb.id)!;
                  const clientValue = getClientValue(kb);
                  const ipValue = getIpValue(kb);
                  const browserValue = getBrowserValue(kb);
                  const userAgentValue = getUserAgentValue(kb);
                  return (
                    <tr key={kb.id} className="hover:bg-gray-50 align-top">
                      {/* KB ID */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{kb.id}</td>
                      {/* KB main */}
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg">
                            {getKbIcon(kb.kbType)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">{kb.name || 'Unnamed KB'}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {kb.description
                                ? kb.description.slice(0, 80) + (kb.description.length > 80 ? '…' : '')
                                : `ID: ${kb.id}`}
                            </div>
                            {/* provider/model chips */}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                                TYPE: {kb.kbType?.toUpperCase() || 'UNKNOWN'}
                              </span>
                              {kb.embedProvider && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800">
                                  PROVIDER: {kb.embedProvider}
                                </span>
                              )}
                              {kb.embedModel && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800">
                                  MODEL: {kb.embedModel}
                                </span>
                              )}
                              {typeof kb.chunkCount === 'number' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                                  CHUNKS: {kb.chunkCount}
                                </span>
                              )}
                              {typeof kb.embeddingDim === 'number' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">
                                  DIM: {kb.embeddingDim}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Sources summary */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <span className="mr-3">Files: <strong>{sum.filesCount}</strong></span>
                          <span>URLs: <strong>{sum.urlsCount}</strong></span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {sum.firstFileName ? `e.g., ${sum.firstFileName}` : sum.firstUrl ? `e.g., ${sum.firstUrl}` : '—'}
                        </div>
                        {(sum.errors > 0 || sum.empties > 0) && (
                          <div className="mt-1 text-xs">
                            {sum.errors > 0 && (
                              <span className="mr-2 px-1.5 py-0.5 rounded bg-red-100 text-red-700">source errors: {sum.errors}</span>
                            )}
                            {sum.empties > 0 && (
                              <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">empty: {sum.empties}</span>
                            )}
                          </div>
                        )}
                        {/* expand/collapse sources */}
                        {kb.sources && kb.sources.length > 0 && (
                          <button
                            className="mt-2 text-xs text-blue-600 hover:underline"
                            onClick={() => setExpanded((e) => ({ ...e, [kb.id]: !e[kb.id] }))}
                          >
                            {expanded[kb.id] ? 'Hide sources' : `View sources (${kb.sources.length})`}
                          </button>
                        )}
                        {expanded[kb.id] && kb.sources && (
                          <ul className="mt-2 max-h-40 overflow-auto text-xs text-gray-700 space-y-1 pr-1">
                            {kb.sources.map((s) => (
                              <li key={s.id} className="flex items-center justify-between">
                                <span className="truncate">
                                  <span className="mr-2">{s.sourceType === 'file' ? '🗎' : '🔗'}</span>
                                  {s.sourceType === 'file'
                                    ? (s.originalFilename || s.storedPath || `file #${s.id}`)
                                    : (s.url || `url #${s.id}`)}
                                </span>
                                <span className="ml-2 shrink-0 text-gray-500">
                                  {typeof s.version === 'number' && (
                                    <span className="mr-2 text-[10px] uppercase tracking-wide text-gray-400">
                                      v{s.version}
                                    </span>
                                  )}
                                  {s.sourceType === 'file' ? formatFileSize(s.size) : s.loader || 'url'}
                                  {s.sourceType === 'file' && s.storedPath && (
                                    <button
                                      type="button"
                                      className="ml-2 text-blue-600 hover:underline"
                                      onClick={() => handleDownloadSource(s)}
                                    >
                                      Download
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="ml-2 text-blue-600 hover:underline"
                                    onClick={() => navigate(`/kb-upload?kbId=${kb.id}&sourceId=${s.id}`)}
                                  >
                                    Replace
                                  </button>
                                  <button
                                    type="button"
                                    className="ml-2 text-red-600 hover:underline disabled:opacity-50"
                                    onClick={() => handleDeleteSource(kb.id, s)}
                                    disabled={deleteSourceLoading === s.id}
                                  >
                                    {deleteSourceLoading === s.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                  {s.status && (
                                    <span className={`ml-2 px-1 rounded ${s.status === 'ok' ? 'bg-green-100 text-green-700' :
                                      s.status === 'empty' ? 'bg-yellow-100 text-yellow-800' :
                                      s.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                      {s.status}
                                    </span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(kb.processingStatus)}</td>

                      {/* Size (sum of file source sizes) */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatFileSize(sum.totalSize)}
                      </td>

                      {/* Client */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {clientValue}
                      </td>

                      {/* Access info */}
                      <td className="px-6 py-4 text-sm text-gray-600 min-w-[240px]">
                        <div className="space-y-1">
                          <p><span className="font-semibold text-gray-800">IP:</span> {ipValue}</p>
                          <p><span className="font-semibold text-gray-800">Browser:</span> {browserValue}</p>
                          <p className="truncate" title={userAgentValue !== 'N/A' ? userAgentValue : undefined}>
                            <span className="font-semibold text-gray-800">UA:</span> {userAgentValue}
                          </p>
                        </div>
                      </td>

                      {/* Updated */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(kb.updatedAt)}
                      </td>

                      {/* Created */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(kb.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/kb-upload?kbId=${kb.id}&mode=add-source`)}
                            className="px-3 py-1 rounded border border-green-200 text-green-700 hover:bg-green-50 transition"
                            title="Add new files or URLs to this knowledge base"
                          >
                            Add Source
                          </button>
                          <button
                            onClick={() => navigate(`/kb-upload?kbId=${kb.id}`)}
                            className="px-3 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
                            title="Update Knowledge Base"
                          >
                            Update
                          </button>
                          <button
                            onClick={() => onNavigateToPlayground && onNavigateToPlayground(kb.id)}
                            disabled={kb.processingStatus !== 'completed'}
                            className={`px-3 py-1 rounded border transition ${
                              kb.processingStatus === 'completed'
                                ? 'text-blue-600 hover:text-blue-900 border-blue-200 hover:bg-blue-50'
                                : 'text-gray-400 border-gray-200 cursor-not-allowed'
                            }`}
                            title={kb.processingStatus === 'completed' ? 'Use in Playground' : 'KB is not ready yet'}
                          >
                            ▶️
                          </button>
                          <button
                            onClick={() => handleDelete(kb.id, kb.name || 'Unnamed KB')}
                            disabled={deleteLoading === kb.id}
                            className="text-red-600 hover:text-red-900 px-3 py-1 rounded border border-red-200 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Knowledge Base"
                          >
                            {deleteLoading === kb.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                            ) : (
                              '🗑️'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats */}
      {knowledgeBases.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <span className="h-8 w-8 text-2xl">📚</span>
              <div className="ml-3">
                <p className="text-blue-600 font-medium">Total KBs</p>
                <p className="text-2xl font-bold text-blue-900">{knowledgeBases.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <span className="h-8 w-8 text-2xl">✅</span>
              <div className="ml-3">
                <p className="text-green-600 font-medium">Ready KBs</p>
                <p className="text-2xl font-bold text-green-900">
                  {knowledgeBases.filter((kb) => kb.processingStatus === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center">
              <span className="h-8 w-8 text-2xl">⏳</span>
              <div className="ml-3">
                <p className="text-yellow-600 font-medium">Processing</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {knowledgeBases.filter((kb) => kb.processingStatus === 'processing' || kb.processingStatus === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <span className="h-8 w-8 text-2xl">💾</span>
              <div className="ml-3">
                <p className="text-purple-600 font-medium">Total Size</p>
                <p className="text-lg font-bold text-purple-900">{formatFileSize(totalBytesAllKBs)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KBList;
